import TextField, { TextFieldVariants } from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useEffect, useState } from 'react';
import { AccountStructure } from '../../models/accounts-structure';
import { SelectTable, Table } from '../../models/table';
import { DocumentStructure } from '../../models/document-structure';
import { Controller, FieldValues, UseFormReturn, useWatch, Path, PathValue, FieldName, FieldErrors } from 'react-hook-form';
import { FormHelperText, InputAdornment } from '@mui/material';
import { ManufacturerModel } from '../../models/manufacturer-model';
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import { checkInputValue } from '../../helpers/helpers';
import { CssDatePicker, CssField, CssLTRField, CssTimePicker } from './MuiInput.styled';
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../services/errorHandling';
import { getManfacturerId } from './MuiInput.utils';
import { isPercentageField, isPriceField } from './MuiInput.types';
import { useSelectTableByNumber } from '../../redux/api/select-tables.api';
import { useLazyGetManufacturerModelsByIdQuery, useLazyGetTableOptionsByTableIndexQuery } from '../../redux/api/system-tables.api';
import AccountSearch from './search/account-search/AccountSearch';
import CarSearch from './search/car-search/CarSearch';
import { TicketHebType } from '../../models/types';
import ItemSearch from './search/item-search/ItemSearch';
import WorkSearch from './search/work-search/WorkSearch';




interface MuiInputProps<T extends FieldValues> {
    field: AccountStructure | DocumentStructure;
    variant: TextFieldVariants;
    size?: "small" | "medium";
    minWidth?: number,
    useForm: UseFormReturn<T>;
    prefix?: string | undefined;
    showRequired?: boolean;
    blockNotEditable?: boolean;
    disabled?: boolean;
    dateToNow?: boolean;
    handleBlur?: (name: Path<T>) => void;
    autoFocus?: boolean;
    searchType?: TicketHebType,
    onSelect?: (object: any) => void;

}

function MuiInput<T extends FieldValues>({ field, variant, size, minWidth=120, useForm, prefix, showRequired = false, blockNotEditable = false, disabled = false, dateToNow = false, handleBlur, autoFocus = false, searchType, onSelect }: MuiInputProps<T>) {

    const { showBoundary } = useErrorBoundary();

    const [getSelectTableData] = useSelectTableByNumber();
    const [getSystemTableData] = useLazyGetTableOptionsByTableIndexQuery();
    const [getManufacturerModels] = useLazyGetManufacturerModelsByIdQuery();

    const fieldName: string = prefix? `${prefix}${field.field_name}` : field.field_name;


    const [table, setTable] = useState<SelectTable[] | ManufacturerModel[] | undefined>();

    const value = useWatch({
        control: useForm.control,
        name: fieldName as Path<T>,
    });



    // run: once, in component creation
    useEffect(() => {
        try {    
            getComponentData();

        } catch (error) {
            handleApiErrors(error, showBoundary);;
        }

    }, []);

    useEffect(()=>{
        if(fieldName === "model" || fieldName === "car.model"){
            console.log("useWatch value",value);

            setManufacturerModelsOptions();
        }
    },[value]);



    // if user changed manufacturer, get models of the selected manufacturer and reset model input value to ""
    useEffect(() => {
        
        if (field.field_type !== 6) return;

        const subscription = useForm.watch((_, { name }) => {

            if (name === "manufacturer" || name === "car.manufacturer") {
                
                setManufacturerModelsOptions();
                useForm.setValue(fieldName as Path<T>, "" as PathValue<T, Path<T>>);

            }
        });

        return () => subscription.unsubscribe();

    }, [useForm]);
    // }, [useForm.watch()]);


    
    useEffect(() => {


        if ((dateToNow && (field.field_type === 3 || field.field_type === 10) && !useForm.formState.defaultValues?.[fieldName])) {
            useForm.setValue(fieldName as Path<T>, dayjs().toDate() as PathValue<T, Path<T>>);

            const interval = setInterval(() => {
                if (!isFieldTouched(useForm.formState.touchedFields as Partial<T>, fieldName) && !isFieldDirty(useForm.formState.dirtyFields as Partial<T>, fieldName)) {
                    useForm.setValue(fieldName as Path<T>, dayjs().toDate() as PathValue<T, Path<T>>);
                }

            }, 1000); // Update every minute

            return () => clearInterval(interval);
        }

    }, []);


    /**
     * if the type is select input, get select options.
     * set "table" state with data.
     */
    async function getComponentData(): Promise<void>{
        setSelectFieldOptions();
        setManufacturerModelsOptions();
    }


    /**
     * if: it's select field
     * do: set field options with data.
     */
    async function setSelectFieldOptions(): Promise<void>{
        if ((field.field_type === 4 || field.field_type === 5) &&  field.field_table && !table) {

            const tableData: SelectTable[] = await getSelectTableData(field.field_table);
            setTable(tableData);
        }
    }
        

    /**
     * if: this is select model field
     * do: set model options by manufacturer chosen
     */
    async function setManufacturerModelsOptions(): Promise<void>{

        if (field.field_type === 6) {

            const manufacturersTable: Table[] = await getSystemTableData(1, true).unwrap();
            const manufacturerID: number | undefined = getManfacturerId(useForm, manufacturersTable);

            console.log("setManufacturerModelsOptions manufacturerID:", manufacturerID);

            if(manufacturerID){
                setTable(await getManufacturerModels(manufacturerID, true).unwrap());
            }
        }
    }


    function isFieldDirty<T extends Record<string, any>>(dirtyFields: Partial<T>, fieldName: keyof T): boolean {
        return !!dirtyFields[fieldName];
    }


    function isFieldTouched<T extends Record<string, any>>(touchedFields: Partial<T>, fieldName: keyof T): boolean {
        return !!touchedFields[fieldName];
    }

    function getFieldByType(field: AccountStructure | DocumentStructure){

        switch (field.field_type) {
            case 1:
                return getHebrewField(field);

            case 2:
                return getEnglishField(field);

            case 3: //date

                return <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                    <Controller
                        name={fieldName as Path<T>}
                        control={useForm?.control}

                        render={({ field: { onChange, onBlur, value } }) => (

                            <CssDatePicker
                                label={field.field_display_name}
                                sx={{ width: 'auto' }}
                                value={
                                    value ? dayjs(value) : null
                                }
                                onOpen={onBlur}
                                onChange={onChange}
                                format="DD/MM/YYYY"
                                disabled={disabled || (blockNotEditable && !field.editable)}

                                slotProps={{
                                    textField: { variant: variant },
                                    actionBar: { actions: ['today', 'cancel', 'clear'] },


                                }}

                                localeText={{
                                    todayButtonLabel: 'היום',
                                    cancelButtonLabel: 'ביטול',
                                    clearButtonLabel: 'איפוס'
                                }}


                            />
                        )} />
                </LocalizationProvider >




            case 4: case 6: // select index or degem
                {
                    return table ?

                        <CssField variant={variant} sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false} disabled={disabled || (blockNotEditable && !field.editable)}>
                            <InputLabel>{field?.field_display_name}{field.required && showRequired ? "*" : ""}</InputLabel>
                            <Controller

                                name={fieldName as Path<T>}
                                control={useForm?.control}
                                defaultValue={"" as PathValue<T, Path<T>>}

                                render={({ field: { onChange, onBlur, value, name } }) => (
                                    <Select
                                        value={checkInputValue(value, "index")} //  ( null => "") || ( "" => "") || ( 0 => "" ) || (number => number)
                                        onChange={onChange}
                                        onBlur={() => { onBlur(); handleBlur?.(name); }}
                                        id={field?.field_name}
                                        size={size}
                                        label={field?.field_display_name}

                                    >
                                        <MenuItem value={""}></MenuItem>
                                        {
                                            table?.map((tableItem, index) => (
                                                <MenuItem key={index} value={tableItem.index}>{index + 1}. {tableItem.name}</MenuItem>
                                            ))
                                        }
                                    </Select>
                                )}
                            />
                            {/* <FormHelperText>{useForm?.formState?.errors?.[fieldName]?.message as string}</FormHelperText> */}
                            <FormHelperText><ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} /></FormHelperText>
                        </CssField>

                        :

                        <CssField variant={variant} sx={{ minWidth }}>
                            <InputLabel>{field?.field_display_name}{field.required && showRequired ? "*" : ""}</InputLabel>
                            <Select
                                value={""}
                                size={size}
                                label={field?.field_display_name}
                            ></Select>
                        </CssField>

                }

            case 5: // select (value)
                {
                    return table ?

                        <CssField variant={variant} sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false}>
                            <InputLabel>{field?.field_display_name}{field.required && showRequired ? "*" : ""}</InputLabel>
                            <Controller

                                name={fieldName as Path<T>}
                                control={useForm?.control}
                                defaultValue={"" as PathValue<T, Path<T>>}

                                render={({ field: { onChange, onBlur, value, name } }) => (
                                    <Select
                                        // defaultValue={""}
                                        value={checkInputValue(value)} //  ( null => "") || ( "" => "") || ( 0 => "" ) || (number => number)
                                        onChange={onChange}
                                        onBlur={() => { onBlur(); handleBlur?.(name); }}
                                        // onChange={(e) => {
                                        //     if (!isNaN(parseInt(e.target.value))) {
                                        //         onChange(parseInt(e.target.value))
                                        //     }
                                        //     if (e.target.value === "") {
                                        //         onChange("")
                                        //     }
                                        // }} // Convert value to number on change
                                        id={field?.field_name}
                                        size={size}
                                        label={field?.field_display_name}
                                        disabled={disabled || (blockNotEditable && !field.editable)}
                                    >
                                        <MenuItem value={""}></MenuItem>
                                        {
                                            table?.map((tableItem, index) => (
                                                <MenuItem key={index} value={tableItem.name || ""}>{tableItem.name}</MenuItem>
                                            ))
                                        }
                                    </Select>
                                )}
                            />
                            {/* <FormHelperText>{useForm?.formState?.errors?.[fieldName]?.message as string}</FormHelperText> */}
                            <FormHelperText><ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} /></FormHelperText>
                        </CssField>

                        :

                        <CssField variant={variant} sx={{ minWidth }}>
                            <InputLabel>{field?.field_display_name}{field.required && showRequired ? "*" : ""}</InputLabel>
                            <Select
                                value={""}
                                size={size}
                                label={field?.field_display_name}
                            ></Select>
                        </CssField>

                }



            case 7: //number

                return getNumberField(field);


            case 10: //time
                return <LocalizationProvider dateAdapter={AdapterDayjs} >
                    <Controller
                        name={fieldName as Path<T>}
                        control={useForm?.control}

                        render={({ field: { onChange, onBlur, value } }) => (

                            <CssTimePicker
                                label={field.field_display_name}
                                // sx={{ direction: "rtl" }}
                                value={
                                    dayjs(value).isValid() ? dayjs(value) : null
                                    // getDateTimeValue(value)
                                }
                                onChange={onChange}
                                // onSelectedSectionsChange={onBlur}
                                onOpen={onBlur}
                                // onSelectedSectionsChange={onBlur}
                                disabled={disabled || (blockNotEditable && !field.editable)}
                                onViewChange={onBlur}
                                format="HH:mm"
                                slotProps={{
                                    textField: { variant: variant },
                                    actionBar: {
                                        actions: ['cancel', 'accept'],
                                    },
                                }}
                                ampm={false}
                                localeText={{
                                    cancelButtonLabel: 'ביטול',
                                    okButtonLabel: 'אישור'
                                }}

                            />

                        )} />
                </LocalizationProvider >

            case 21: // true/false select
                return <CssField variant={variant} sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false}>
                    <InputLabel>{field?.field_display_name}{field.required && showRequired ? "*" : ""}</InputLabel>
                    <Controller

                        name={fieldName as Path<T>}
                        control={useForm?.control}
                        defaultValue={"" as PathValue<T, Path<T>>}

                        render={({ field: { onChange, onBlur, value, name } }) => (
                            <Select
                                // defaultValue={""}
                                value={checkInputValue(value, 'number')} // (null || ""  => "" )  ||  ( boolean => number: 0 || 1 ) || ( number => number: 0 || 1 )
                                // onChange={onChange}
                                onChange={(e) => onChange(e.target.value === "" ? "" : !!e.target.value)} // ( "" => "")  ||  ( 0 || 1 => boolean: true || false )
                                onBlur={() => { onBlur(); handleBlur?.(name); }}
                                id={field?.field_name}
                                size={size}
                                label={field?.field_display_name}
                                disabled={disabled || (blockNotEditable && !field.editable)}
                            >
                                <MenuItem value={""}></MenuItem>
                                <MenuItem value={0}>לא</MenuItem>
                                <MenuItem value={1}>כן</MenuItem>
                            </Select>
                        )}
                    />
                    {/* <FormHelperText>{useForm?.formState?.errors?.[field.field_name]?.message as string}</FormHelperText> */}
                    <FormHelperText><ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} /></FormHelperText>

                </CssField>
        
            default:
                break;
        }
    }

    function getSearchField(field: AccountStructure | DocumentStructure){

        if(onSelect && searchType){

            switch (searchType) {
                case "לקוח-ספק":
                case "בנק":
                    return <AccountSearch
                        accountHebType={searchType}
                        fieldStructure={field}
                        useForm={useForm}
                        onSelect={onSelect}
                        prefixPath={prefix}
                        disabled={disabled}
                    />
                
                case "רכב":
                    return <CarSearch 
                        fieldStructure={field}
                        useForm={useForm}
                        onSelect={onSelect}
                        prefixPath={prefix}
                        disabled={disabled}
                    />

                case "פריט":
                    return <ItemSearch 
                        fieldStructure={field}
                        useForm={useForm}
                        onSelect={onSelect}
                        prefixPath={prefix}
                        disabled={disabled}
                    />

                case "עבודה":
                    return <WorkSearch
                        fieldStructure={field}
                        useForm={useForm}
                        onSelect={onSelect}
                        prefixPath={prefix}
                        disabled={disabled}
                    />

                default:
                    break;
            }
        }
        

        
    }

    function getHebrewField(field: AccountStructure | DocumentStructure){

        
        
        if(onSelect){ 
            return getSearchField(field);
        }

        return <CssField sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false}>
            <Controller

                name={fieldName as Path<T>}
                control={useForm?.control}
                defaultValue={"" as PathValue<T, Path<T>>}

                render={({ field: { onChange, onBlur, value, name } }) => (
                    <TextField

                        id={field?.field_name}
                        value={checkInputValue(value)}
                        onChange={onChange}
                        onBlur={() => { onBlur(); handleBlur?.(name); }}

                        variant={variant}
                        size={size}
                        label={field?.field_display_name}
                        disabled={disabled || (blockNotEditable && !field.editable)}
                        required={field.required && showRequired ? true : undefined}
                        autoComplete="off"

                        error={useForm?.formState?.errors?.[fieldName] ? true : false}
                        helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                    />
                )}
            />
        </CssField>
    }

    
    function getEnglishField(field: AccountStructure | DocumentStructure){

        if(onSelect){
            return getSearchField(field);
        }

        return <CssField sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false}>
            <Controller

                name={fieldName as Path<T>}
                control={useForm?.control}
                defaultValue={"" as PathValue<T, Path<T>>}

                render={({ field: { onChange, onBlur, value, name } }) => (
                    <TextField

                        id={field?.field_name}
                        value={checkInputValue(value)} //  ( null => "") || ( "" => "") || ( 0 => "" ) || (number => number)
                        onChange={onChange}
                        onBlur={() => { onBlur(); handleBlur?.(name); }}

                        variant={variant}
                        size={size}
                        label={field?.field_display_name}
                        disabled={disabled || (blockNotEditable && !field.editable)}
                        required={field.required && showRequired ? true : undefined}
                        autoComplete="off"
                        sx={{ direction: 'rtl' }}

                        error={useForm?.formState?.errors?.[fieldName] ? true : false}
                        helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                    />
                )}
            />
        </CssField>
    }

    function getNumberField(field: AccountStructure | DocumentStructure){
        
        if(onSelect){
            return getSearchField(field);
        }

        return <CssField sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldName] ? true : false}>
            <Controller

                name={fieldName as Path<T>}
                control={useForm?.control}
                // defaultValue={"" as PathValue<T, Path<T>>}

                render={({ field: { onChange, onBlur, value, name } }) => (
                    <CssLTRField

                        id={field?.field_name}
                        // value={value} //  ( null => "") || ( "" => "") || ( 0 => "" ) || (number => number)
                        value={checkInputValue(value, 'number')} //  ( null => "") || ( "" => "") || ( 0 => "" ) || (number => number)
                        // onChange={onChange}
                        onChange={(e) => {
                            onChange(checkInputValue(e.target.value, 'number'))
                        }}
                        onBlur={() => { onBlur(); handleBlur?.(name); }}
                        type="number"

                        variant={variant}
                        size={size}
                        autoFocus={autoFocus}
                        label={field?.field_display_name}
                        disabled={disabled || (blockNotEditable && !field.editable)}
                        required={field.required && showRequired ? true : undefined}
                        autoComplete="off"
                        sx={{ direction: 'rtl' }}
                        InputProps={isPriceField(field.field_name) ? {
                            startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                        } : isPercentageField(field.field_name) ? {
                            startAdornment: <InputAdornment position="start">%</InputAdornment>,
                        } : {}}


                        error={useForm?.formState?.errors?.[fieldName] ? true : false}
                        helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldName as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                    />
                )}
            />
        </CssField>
    }


    return getFieldByType(field);

}


export default MuiInput